import {matchAllMetrics} from '../../state/interactState/constants';
import {
  type HighlightSource,
  type SetHighlight,
  type SetHighlights,
} from '../../state/interactState/highlightSource';
import {
  ALL_METRICS,
  type GraphInteraction,
  type GraphInteractionSource,
  type GraphMetric,
} from '../vis/interaction';

/**
 * Connects Graph to the application's shared hover and crosshair state.
 *
 * Chart components should not need the application's highlight keys, setters,
 * or all-metrics sentinel. This adapter translates between those details and
 * the chart-facing contract in vis/interaction.ts. The same renderer can then
 * use shared application state or a standalone host's local interaction state.
 *
 * Pointer positions change frequently. Passing their values through Graph
 * would make it participate in those updates. The existing source-based design
 * instead lets each consumer subscribe to the value it draws: LinePlot reads
 * the highlighted series/metric, while LinePlotCrosshair reads its own x axis.
 * This adapter preserves that design; it does not introduce the subscription
 * optimization. An x-only update need not rerender LinePlot or Graph.
 *
 * The application remains the owner of shared state. Adapting a source neither
 * reads a snapshot nor subscribes, and events forward writes to the supplied
 * setters. Pointer movement updates x and any nearest-series highlight together.
 */
export type GraphHighlightInputs = {
  source: HighlightSource;
  setHighlight: SetHighlight;
  setHighlights: SetHighlights;
};

// Both series hover and pointer movement can highlight a series across metrics.
// Translate the chart-owned sentinel to the value the application reducers use,
// so chart components do not need to import application state constants.
function toApplicationMetric(
  metric: GraphMetric | undefined
): Parameters<SetHighlight>[2] {
  return metric === ALL_METRICS ? matchAllMetrics : metric;
}

const sources = new WeakMap<HighlightSource, GraphInteractionSource>();

/** Reuses the same readers and subscription function for each application source. */
export function getGraphInteractionSource(
  source: HighlightSource
): GraphInteractionSource {
  const existing = sources.get(source);
  if (existing != null) {
    return existing;
  }
  const mapped: GraphInteractionSource = {
    getHighlightedId: () => source.get()['run:name'],
    getHighlightedMetric() {
      const metric = source.get()['run:metric'];
      return metric === matchAllMetrics ? ALL_METRICS : metric;
    },
    getX: axis => source.get()[axis] as number | undefined,
    subscribe: source.subscribe,
  };
  sources.set(source, mapped);
  return mapped;
}

/**
 * Pairs the cached reader adapter with callbacks for the supplied store writers.
 * Only the source is cached here; each call creates new event callbacks.
 */
export function getGraphInteraction(
  inputs: GraphHighlightInputs
): GraphInteraction {
  function onSeriesHover(
    id: string | undefined,
    metric: GraphMetric | undefined
  ) {
    inputs.setHighlight('run:name', id, toApplicationMetric(metric));
  }
  function onPointerMove({
    axis,
    x,
    highlight,
  }: Parameters<GraphInteraction['events']['onPointerMove']>[0]) {
    const updates: Parameters<SetHighlights>[0] = [{axis, value: x}];
    if (highlight != null) {
      updates.push({
        axis: 'run:name',
        value: highlight.id,
        metric: toApplicationMetric(highlight.metric),
      });
    }
    inputs.setHighlights(updates);
  }
  function onPointerLeave(axis: string) {
    inputs.setHighlights([
      {axis, value: undefined},
      {axis: 'run:name', value: undefined},
    ]);
  }
  return {
    source: getGraphInteractionSource(inputs.source),
    events: {onSeriesHover, onPointerMove, onPointerLeave},
  };
}
